/* @jsx h */
import { computed, defineComponent, h } from "vue";
import { Order } from "@lemmonhq/types";
import { formatPrice, getOrderDisplayName } from "@lemmonhq/orders";
import { useCustomer } from "@app/vue/providers/Auth";
import { useLocalization } from "@lemmonhq/uikit";

interface OrderHistoryCardProps {
  order: Order;
  restaurantName: string;
  currency: string;
  /** Payment the customer was identified on; links to its receipt page. */
  receiptGuid?: string | null;
}

// todo: extreact it out
const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case "closed":
    case "delivered":
      return "text-success bg-success/10";
    case "pending":
    case "preparing":
      return "text-warning bg-warning/10";
    case "cancelled":
      return "text-error bg-error/10";
    default:
      return "text-base-content/60 bg-base-200";
  }
};

export const OrderHistoryCard = defineComponent<OrderHistoryCardProps>({
  props: ["order", "restaurantName", "currency", "receiptGuid"],
  setup(props) {
    const { customer } = useCustomer();
    const { locale, t } = useLocalization();
    const currency = computed(() => props.currency || "CHF");
    const receiptUrl = computed(() => {
      if (props.receiptGuid) return `/payments/${props.receiptGuid}`;
      if (customer.value && props.order.guid) {
        return `/api/v3/customer/payments/receipt/${props.order.guid}/${customer.value.id}`;
      }
      return null;
    });

    const getServiceMethodIcon = (method: string) => {
      switch (method.toLowerCase()) {
        case "delivery":
          return "local_shipping";
        case "takeaway":
          return "takeout_dining";
        case "dine-in":
          return "restaurant";
        default:
          return "shopping_bag";
      }
    };

    const formatLocalDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString(locale.value || "en", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    const getServiceMethodLabel = (method: string) => {
      switch (method.toLowerCase()) {
        case "delivery":
          return t("customer.account.delivery");
        case "takeaway":
          return t("customer.account.pickup");
        case "dine-in":
          return t("customer.account.dine_in");
        default:
          return method;
      }
    };

    const getStatusLabel = (status: string) => {
      switch (status.toLowerCase()) {
        case "closed":
          return t("customer.account.order_status_closed");
        case "delivered":
          return t("customer.account.order_status_delivered");
        case "pending":
          return t("customer.account.order_status_pending");
        case "preparing":
          return t("customer.account.order_status_preparing");
        case "cancelled":
          return t("customer.account.order_status_cancelled");
        default:
          return status.charAt(0).toUpperCase() + status.slice(1);
      }
    };

    return () => (
      <article class="overflow-hidden rounded-2xl bg-base-200/60">
        <div class="flex items-start gap-3 p-4">
          <span
            class="material-symbols-outlined grid size-10 shrink-0 place-items-center rounded-xl bg-base-100 text-xl text-base-content/55 shadow-sm"
            aria-hidden="true"
          >
            {getServiceMethodIcon(props.order.service_method || "")}
          </span>
          <div class="min-w-0 flex-1">
            <p class="truncate text-[15px] font-semibold text-base-content">
              {props.restaurantName}
            </p>
            <p class="mt-0.5 truncate text-xs text-base-content/50">
              {getOrderDisplayName(props.order)} ·{" "}
              {formatLocalDate(props.order.created_at)}
            </p>
          </div>
          <div class="shrink-0 text-right">
            <p class="text-[15px] font-bold text-base-content">
              {formatPrice(props.order.amount, true, 2, currency.value)}
            </p>
            <span
              class={[
                "mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold",
                getStatusColor(props.order.status || ""),
              ]}
            >
              {getStatusLabel(props.order.status || "")}
            </span>
          </div>
        </div>

        {props.order.items && props.order.items.length > 0 && (
          <ul class="mx-4 space-y-1.5 rounded-xl bg-base-100 p-3 text-xs">
            {props.order.items.slice(0, 3).map((item, index) => (
              <li key={index} class="flex items-center justify-between gap-3">
                <span class="flex min-w-0 items-center gap-2">
                  <span class="grid size-5 shrink-0 place-items-center rounded bg-base-200 font-medium">
                    {item.quantity}
                  </span>
                  <span class="truncate font-medium text-base-content/70">
                    {item.product.name}
                  </span>
                </span>
                <span class="shrink-0 text-base-content/60">
                  {formatPrice(item.price, true, 2, currency.value)}
                </span>
              </li>
            ))}
            {props.order.items.length > 3 && (
              <li class="border-t border-base-content/5 pt-1.5 text-base-content/50">
                {t("customer.account.more_items", [
                  props.order.items.length - 3,
                ])}
              </li>
            )}
          </ul>
        )}

        <div class="flex items-center justify-between gap-3 px-4 py-3">
          <span class="text-xs text-base-content/50">
            {getServiceMethodLabel(props.order.service_method || "")} ·{" "}
            {t(
              (props.order.items?.length || 0) === 1
                ? "customer.account.item_count_one"
                : "customer.account.item_count_many",
              [props.order.items?.length || 0],
            )}
          </span>
          {receiptUrl.value && (
            <a
              href={receiptUrl.value}
              target="_blank"
              class="inline-flex items-center gap-1 text-sm font-semibold text-primary transition-colors hover:text-primary/80"
            >
              <span
                class="material-symbols-outlined text-base"
                aria-hidden="true"
              >
                receipt
              </span>
              {t("customer.account.view_receipt")}
            </a>
          )}
        </div>
      </article>
    );
  },
});
